import axios, { AxiosResponse } from 'axios';

import Constants from './Constants';
import { SharePointService } from './SharePointService';

export class AuthService {
  private static _instance: AuthService;

  private expirationIntervalId: NodeJS.Timeout | null = null;
  private expirationTimeInMinutes: number | null = null;

  private constructor() {}

  public static get Instance() {
    return this._instance || (this._instance = new this());
  }

  public async initialize() {
    if (this.expirationTimeInMinutes !== null) {
      return;
    }
    try {
      const baseUrl = window.location.origin + '/api/WHCPortal';
      const result: AxiosResponse<any> = await axios({
        method: 'get',
        url: `${baseUrl}/GetAuthSettings`,
        withCredentials: true,
      });
      const authSettings = result.data;
      this.expirationTimeInMinutes = authSettings.expirationTimeInMinutes;
    } catch (error) {
      console.error(
        'Failed to initialize AuthService auth settings from SharePoint.',
        error
      );
      console.warn('Defaulting to 30 minutes for session expiration.');
      this.expirationTimeInMinutes = 30; // Default to 30 minutes as a fallback
    }
  }

  private startExpirationTimer() {
    if (this.expirationIntervalId) {
      clearInterval(this.expirationIntervalId);
    }
    this.expirationIntervalId = setInterval(() => {
      this.checkExpiration();
    }, 15000); // Check every 15 seconds
  }

  private stopExpirationTimer() {
    if (this.expirationIntervalId) {
      clearInterval(this.expirationIntervalId);
      this.expirationIntervalId = null;
    }
  }

  public setExpirationTime() {
    if (this.expirationTimeInMinutes === null) {
      return;
    }
    const now = new Date();
    const expirationTime =
      now.getTime() + this.expirationTimeInMinutes * 60 * 1000;
    localStorage.setItem(
      Constants.LOCAL_STORAGE_KEYS.EXPIRATION_TIME,
      expirationTime.toString()
    );
  }

  public checkExpiration() {
    const isDevelopment = process.env.REACT_APP_IS_DEVELOPMENT === 'true';

    const expirationTime = localStorage.getItem(
      Constants.LOCAL_STORAGE_KEYS.EXPIRATION_TIME
    );
    if (expirationTime) {
      const now = new Date().getTime();
      if (now > parseInt(expirationTime)) {
        this.removeExpirationTime();
        localStorage.setItem(
          Constants.LOCAL_STORAGE_KEYS.IS_SESSION_TIMED_OUT,
          'true'
        );
        if (isDevelopment) {
          alert('Session expired. Please refresh the page.');
          return;
        }
        window.location.href = Constants.LOGOUT_REDIRECT_URI;
      }
    }
  }

  public removeExpirationTime() {
    localStorage.removeItem(Constants.LOCAL_STORAGE_KEYS.EXPIRATION_TIME);
    this.stopExpirationTimer();
  }

  public async getCurrentUserName(): Promise<string | null> {
    this.checkExpiration();

    const isDevelopment = process.env.REACT_APP_IS_DEVELOPMENT === 'true';
    if (isDevelopment) {
      this.setExpirationTime();
      this.startExpirationTimer();
      this.clearTimeoutFlag();
      return 'Development User'; // For development purposes, return a static user name
    }

    try {
      const result: AxiosResponse<any> = await axios({
        method: 'get',
        url: `/.auth/me`,
        withCredentials: true,
      });

      if (result.data.length === 0 || result.data[0].user_claims.length === 0) {
        this.removeExpirationTime();
        return null;
      }

      this.setExpirationTime();
      this.startExpirationTimer();
      this.clearTimeoutFlag();

      return result.data[0].user_claims.find(
        (claim: any) => claim.typ === 'name'
      )?.val;
    } catch (error) {
      console.error('Failed to get current user name', error);
      this.removeExpirationTime();
      return null;
    }
  }

  public checkIsSessionTimedOut(): boolean {
    const isTimeout = localStorage.getItem(
      Constants.LOCAL_STORAGE_KEYS.IS_SESSION_TIMED_OUT
    );
    return isTimeout === 'true';
  }

  public clearTimeoutFlag() {
    localStorage.removeItem(Constants.LOCAL_STORAGE_KEYS.IS_SESSION_TIMED_OUT);
  }
}
